/**
 * Centralized session-invalidation helper.
 *
 * Every code path that decides "this session is over" (bootstrap
 * failure, interceptor 401-then-refresh-failure, manual user logout)
 * runs through this function. The two steps are not optional and must
 * always happen in this order:
 *
 *   1. Clear the full TanStack Query cache. Scoping the wipe to
 *      auth-only queries is too weak — a previous user's
 *      business / product / customer data could otherwise linger and
 *      briefly leak into the next login.
 *   2. Clear the in-memory access token + user from the auth store so
 *      the route guards see a clean guest state.
 *
 * The helper does NOT redirect. The caller decides whether to
 * `navigate('/login')` (header logout) or a safe login-recovery path
 * (interceptor — forces a clean SessionBootstrap rerun).
 */

import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/state/stores/auth.store';
import { clearNotificationSession } from '@/core/notifications';

interface ClearSessionDeps {
  queryClient: QueryClient;
  authStore?: typeof useAuthStore;
}

export function clearSessionAndCache(deps: ClearSessionDeps): void {
  const store = deps.authStore ?? useAuthStore;
  // Invalidate/dismiss before clearing auth state so a late request from this
  // identity cannot announce itself to the next login.
  clearNotificationSession();
  deps.queryClient.clear();
  store.getState().logout();
}
